import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { clearToken } from '../utils/tokenStorage';
import { Button } from './ui/button';
import { 
  Users, 
  Sparkles, 
  BarChart3, 
  Shield, 
  Ban, 
  FileText,
  FileEdit,
  Home,
  LogOut,
  Menu,
  X,
  Megaphone
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: Home },
  { path: '/admin/artists', label: 'All Artists', icon: Users },
  { path: '/admin/featured', label: 'Featured', icon: Sparkles },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/admins', label: 'Admins', icon: Shield },
  { path: '/admin/cancellations', label: 'Cancellations', icon: Ban },
  { path: '/admin/announcements', label: 'Announcements', icon: Megaphone },
  { path: '/admin/site-content', label: 'Site Content', icon: FileEdit },
  { path: '/admin/moderation', label: 'Content', icon: FileText },
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      clearToken();
      logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Mobile menu button */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed top-20 left-4 z-50 md:hidden bg-violet-600 p-2 rounded-lg"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-16 left-0 h-[calc(100vh-4rem)] w-64 
        bg-black/40 backdrop-blur-xl border-r border-white/10
        transform transition-transform duration-300 ease-in-out z-40
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        {/* User info */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt="Profile" 
                className="w-10 h-10 rounded-full border-2 border-violet-500"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                <Shield className="text-violet-400" size={20} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-violet-400">{user?.role === 'owner' ? 'Owner' : user?.role === 'overseer' ? 'Overseer' : 'Administrator'}</p>
            </div>
            {/* Close button for mobile - positioned to the right */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100%-10rem)]">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                  ${active 
                    ? 'bg-violet-600 text-white' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }
                `}
              >
                <Icon size={18} />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Bottom actions */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-black/40">
          <Button
            onClick={() => navigate('/browse')}
            variant="outline"
            className="w-full mb-2 border-white/20 hover:bg-white/10"
            size="sm"
          >
            View Site
          </Button>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
            size="sm"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="md:ml-64 min-h-[calc(100vh-4rem)]">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
